import {
  carriedEntries,
  getItem,
  getOffer,
  isOnSale,
  type GiftCodeItem,
  type InventoryState,
  SHOP_OFFERS,
  type ShopOffer,
  type ShopOrder,
  type ShopPack,
} from '@shefira/shared';

import type { CssAtlas } from '../assets/dom-atlas.js';
import {
  cancelOrder,
  claimOrder,
  fetchOrder,
  fetchOrders,
  redeemGiftCode,
  startCheckout,
} from '../net/shop.js';
import { CheckoutModal } from './checkout-modal.js';
import { ConsentModal } from './consent-modal.js';
import { ThanksModal } from './thanks-modal.js';
import { AuthError } from '../net/session.js';
import { isAction } from '../settings/keybindings.js';
import { closeButton, hudShortcutButton } from './hud.js';
import {
  button,
  element,
  heroCard,
  packCard,
  pieceCard,
  type ShopCardContext,
} from './shop-cards.js';
import { type ShopPreviewSource, ShopPreviews } from './shop-previews.js';
import { uiFormat, uiText } from './text.js';
import './shop.css';
import { kitTitle } from './kit.js';

export type { ShopPreviewSource } from './shop-previews.js';

/**
 * The shop (ADR 0031): cosmetics paid one offer at a time through Stripe
 * Checkout. Opened by the HUD button in the top row or its key, from anywhere.
 *
 * Two views. The **home** is a storefront: the founder pack alone on its
 * stage, the other packs as cards under it, and one by one only the pieces
 * that belong to no pack (a lone mount). A **pack page** shows that pack's
 * stage again and sells its pieces singly, so the home never floods with a
 * pack's parts. Cards and moving previews live in `shop-cards.ts` and
 * `shop-previews.ts`; this file holds the views, the buying and the orders.
 *
 * Buying sends the browser to Stripe; back here with `?order=<id>`, the
 * window opens and watches the order until the server has put the items in
 * the bag. Like every panel it only asks: prices, ownership and delivery are
 * the server's.
 */

export interface ShopPanelOptions {
  token: string;
  characterName: string;
  icons: CssAtlas | null;
  preview: ShopPreviewSource;
  /** The thank-you window's « Voir mon inventaire »: open it on its look side. */
  onShowInventory: () => void;
  /** A guest was refused at checkout: open the account tab, where an e-mail makes the account real. */
  onCreateAccount: () => void;
}

/** How long the window watches an order after the browser comes back from Stripe. */
const TRACK_EVERY_MS = 2000;
const TRACK_TRIES = 30;

/**
 * An order still `pending` after this long was abandoned: its Checkout lives
 * thirty minutes at Stripe (`CHECKOUT_LIFETIME_S`), then expires and cancels it.
 */
const PENDING_SHOWN_MS = 30 * 60 * 1000;

/** The pack the home opens on, at the top of the window, alone in that place. */
const FEATURED_PACK: ShopPack = 'founder';

const REFUSALS: Record<string, string> = {
  'unknown-offer': 'Cet article n’existe pas.',
  'off-season': 'Cette offre n’est plus en vente pour cette saison.',
  'unknown-order': 'Cette commande n’existe pas.',
  'shop-unavailable': 'La boutique est fermée pour le moment.',
  'not-claimable': 'Cette commande n’est pas à récupérer.',
  'bag-full': 'Ton sac est plein : fais de la place, puis récupère ta commande.',
  'waiver-required':
    'Coche la demande de livraison immédiate et de renonciation au droit de rétractation.',
  'guest-account':
    'Enregistre ton compte (e-mail, Google ou Steam) pour acheter : un compte invité peut se perdre.',
  'unknown-code': 'Ce code n’existe pas.',
  'expired-code': 'Ce code a expiré.',
  'exhausted-code': 'Ce code a déjà été utilisé autant de fois que prévu.',
  'already-redeemed': 'Ton compte a déjà reçu ce cadeau.',
  payment_provider: 'Le paiement est indisponible, réessaie plus tard.',
  unreachable: 'Serveur injoignable.',
};

/** The offer a gift code amounts to, when it gives exactly an offer's items: its thank-you window then fits. */
function offerGiven(items: readonly GiftCodeItem[]): ShopOffer | undefined {
  const key = (list: readonly GiftCodeItem[]): string =>
    list
      .map((item) => `${item.templateId}:${String(item.quantity)}`)
      .sort()
      .join(',');
  const given = key(items);
  return SHOP_OFFERS.find((offer) => key(offer.items) === given);
}

/** The offers the shop shows now: a seasonal pack's are out of its window (ADR 0031). */
function onSale(): ShopOffer[] {
  const now = Date.now();
  return SHOP_OFFERS.filter((offer) => isOnSale(offer, now));
}

/** The bundle offer of a pack: the one that lists several items. */
function bundleOf(pack: ShopPack): ShopOffer | undefined {
  return SHOP_OFFERS.find((offer) => offer.pack === pack && offer.items.length > 1);
}

export class ShopPanel {
  private readonly root: HTMLElement;
  private readonly body: HTMLElement;
  private readonly ordersList: HTMLElement;
  private readonly message: HTMLElement;
  private readonly button: HTMLButtonElement;
  private readonly codeForm: HTMLFormElement;
  private readonly codeLink: HTMLButtonElement;
  private readonly previews: ShopPreviews;
  private inventory: InventoryState | undefined;
  private orders: ShopOrder[] = [];
  private tracking: string | undefined;
  private busy = false;
  /** The pack whose page is open; null on the home. */
  private view: ShopPack | null = null;
  private readonly onKeyDown = (event: KeyboardEvent): void => {
    if (event.metaKey || event.ctrlKey || event.altKey || event.repeat) return;
    if (document.activeElement instanceof HTMLInputElement) return;
    if (event.key === 'Escape' && !this.root.hidden) {
      // Escape steps back before it closes: a pack page returns to the home.
      if (this.view !== null) this.show(null);
      else this.close();
      return;
    }
    if (!isAction(event, 'shop')) return;
    event.preventDefault();
    if (this.root.hidden) this.open();
    else this.close();
  };

  constructor(private readonly options: ShopPanelOptions) {
    this.previews = new ShopPreviews(options.preview);
    this.root = element('section', 'shop');
    this.root.hidden = true;
    this.root.setAttribute('aria-label', uiText('Boutique'));

    const head = element('header', 'shop__head');
    head.append(
      kitTitle(element('h2', 'shop__title', 'Boutique'), 'shop'),
      element(
        'p',
        'shop__hint',
        'Des cosmétiques par-dessus ton équipement : le look change, pas les stats.',
      ),
    );
    // The window keeps no overflow of its own (it would clip the cross on
    // the frame): the body scrolls, the footer stays in view under it.
    this.body = element('div', 'shop__body');
    const foot = element('footer', 'shop__foot');
    this.ordersList = element('ul', 'shop__orders');
    this.message = element('p', 'shop__message');
    this.message.setAttribute('aria-live', 'polite');
    // Few players ever hold a gift code: a small link at the foot, the field only once asked for.
    this.codeForm = this.giftCodeForm();
    this.codeForm.hidden = true;
    this.codeLink = button('shop__link shop__code-link', 'J’ai un code cadeau', () => {
      this.codeLink.hidden = true;
      this.codeForm.hidden = false;
      this.codeForm.querySelector('input')?.focus();
    });
    const legal = element('div', 'shop__legal-row');
    legal.append(
      element(
        'p',
        'shop__legal',
        uiFormat(
          'Paiement sécurisé par Stripe, en euros, livré dans le sac de {name}. Un achat s’échange ou se vend à l’hôtel des ventes quatorze jours après sa livraison, jamais au marchand.',
          { name: options.characterName },
        ),
      ),
      this.codeLink,
    );
    foot.append(this.ordersList, this.codeForm, this.message, legal);
    this.root.append(
      closeButton('Fermer (Échap)', () => {
        this.close();
      }),
      head,
      this.body,
      foot,
    );
    document.body.append(this.root);

    this.button = hudShortcutButton('shop', 'Boutique', 'shop', 'top');
    // A deal on (a bundle under the worth of its pieces): a small flame
    // flickers in the corner of the button, so the offer is seen from play.
    if (SHOP_OFFERS.some((offer) => offer.worthCents !== undefined)) {
      const badge = document.createElement('span');
      badge.className = 'hud-badge-flame';
      badge.setAttribute('aria-hidden', 'true');
      this.button.classList.add('hud-button--deal');
      this.button.append(badge);
    }
    this.button.addEventListener('click', () => {
      if (this.root.hidden) this.open();
      else this.close();
    });
    window.addEventListener('keydown', this.onKeyDown);
  }

  get isOpen(): boolean {
    return !this.root.hidden;
  }

  /** Opens on the home, whatever page was last looked at. */
  open(): void {
    this.root.hidden = false;
    this.message.textContent = '';
    this.codeForm.hidden = true;
    this.codeLink.hidden = false;
    this.view = null;
    this.render();
    this.previews.start();
    void this.refreshOrders();
  }

  close(): void {
    this.root.hidden = true;
    this.previews.stop();
  }

  /** What the bag holds decides which offers read as already owned. */
  setInventory(state: InventoryState): void {
    this.inventory = state;
    if (!this.root.hidden) this.render();
  }

  /**
   * Back from Stripe: watch the order until the server has delivered it, or
   * has recorded it paid with a full bag, and say so.
   */
  trackOrder(orderId: string): void {
    this.open();
    this.tracking = orderId;
    this.say('Paiement en cours de confirmation…', 'muted');
    let tries = 0;
    const poll = (): void => {
      if (this.tracking !== orderId) return;
      tries += 1;
      fetchOrder(this.options.token, orderId)
        .then((order) => {
          if (this.tracking !== orderId) return;
          const name = getOffer(order.offerId)?.name ?? order.offerId;
          if (order.status === 'delivered') {
            this.tracking = undefined;
            this.say(uiFormat('Livré dans ton sac : {name}. Merci !', { name }), 'success');
            void this.refreshOrders();
            this.celebrate(order.offerId);
          } else if (order.status === 'paid') {
            this.tracking = undefined;
            this.say(
              uiFormat(
                '{name} est payé, mais ton sac est plein : fais de la place, puis récupère-le ci-dessous.',
                { name },
              ),
              'refused',
            );
            void this.refreshOrders();
          } else if (order.status === 'cancelled') {
            this.tracking = undefined;
            this.say('Paiement annulé.', 'refused');
          } else if (tries < TRACK_TRIES) {
            window.setTimeout(poll, TRACK_EVERY_MS);
          } else {
            this.tracking = undefined;
            this.say(
              'Le paiement n’est pas encore confirmé. Il arrivera dans ton sac dès que Stripe l’aura validé.',
              'muted',
            );
          }
        })
        .catch((error: unknown) => {
          this.tracking = undefined;
          this.fail(error);
        });
    };
    poll();
  }

  showCancelled(): void {
    this.open();
    this.say('Paiement annulé : rien n’a été débité.', 'refused');
  }

  destroy(): void {
    this.previews.stop();
    window.removeEventListener('keydown', this.onKeyDown);
    this.button.remove();
    this.root.remove();
  }

  private say(text: string, tone: 'success' | 'refused' | 'muted'): void {
    this.message.textContent = uiText(text);
    this.message.dataset.tone = tone;
  }

  private fail(error: unknown): void {
    const code = error instanceof AuthError ? error.code : '';
    if (code === 'guest-account') {
      // A guest's purchase would lapse with its token (ADR 0044): the way out is one click away.
      this.say(REFUSALS['guest-account'] ?? '', 'refused');
      this.message.append(
        button('shop__claim shop__message-action', 'Créer mon compte', () => {
          this.close();
          this.options.onCreateAccount();
        }),
      );
      return;
    }
    const known = REFUSALS[code];
    this.say(
      known ?? (error instanceof AuthError ? error.message : 'Action impossible.'),
      'refused',
    );
  }

  /** A gift code (ADR 0063): typed here, checked and given by the server. */
  private giftCodeForm(): HTMLFormElement {
    const form = element('form', 'shop__code');
    const input = document.createElement('input');
    input.className = 'shop__code-input';
    input.type = 'text';
    input.maxLength = 64;
    input.autocomplete = 'off';
    input.spellcheck = false;
    input.placeholder = uiText('Code cadeau');
    input.setAttribute('aria-label', uiText('Code cadeau'));
    const submit = element('button', 'shop__claim', 'Valider');
    submit.type = 'submit';
    form.append(input, submit);
    form.addEventListener('submit', (event) => {
      event.preventDefault();
      const code = input.value.trim();
      if (code === '' || submit.disabled) return;
      submit.disabled = true;
      this.say('Vérification du code…', 'muted');
      redeemGiftCode(this.options.token, code)
        .then((items) => {
          input.value = '';
          const offer = offerGiven(items);
          if (offer !== undefined) {
            this.celebrate(offer.id);
            return;
          }
          const names = items.map((item) => {
            const name = getItem(item.templateId)?.name ?? item.templateId;
            return item.quantity > 1 ? `${name} ×${String(item.quantity)}` : name;
          });
          this.say(
            uiFormat('Code accepté : {names} dans ton sac.', { names: names.join(', ') }),
            'success',
          );
        })
        .catch((error: unknown) => {
          // A full bag here is no order to claim later: the server's own words say what to do.
          if (error instanceof AuthError && error.code === 'bag-full') {
            this.say(error.message, 'refused');
          } else {
            this.fail(error);
          }
        })
        .finally(() => {
          submit.disabled = false;
        });
    });
    return form;
  }

  private ownedTemplates(): Set<string> {
    const owned = new Set<string>();
    if (this.inventory === undefined) return owned;
    for (const entry of carriedEntries(this.inventory)) owned.add(entry.item.templateId);
    for (const instance of Object.values(this.inventory.equipped)) owned.add(instance.templateId);
    return owned;
  }

  /** Switch view and start reading it from the top. */
  private show(view: ShopPack | null): void {
    this.view = view;
    this.render();
    this.body.scrollTop = 0;
  }

  private render(): void {
    this.previews.clear();
    this.body.replaceChildren();
    const context: ShopCardContext = {
      previews: this.previews,
      icons: this.options.icons,
      owned: this.ownedTemplates(),
      busy: this.busy,
      buy: (offer) => {
        this.buy(offer);
      },
      openPack: (pack) => {
        this.show(pack);
      },
    };
    if (this.view === null) this.renderHome(context);
    else this.renderPack(this.view, context);
    this.renderOrders();
  }

  /** A titled section of the body: the heading, then a container of cards. */
  private section(title: string, className: string, cards: HTMLElement[]): void {
    if (cards.length === 0) return;
    const container = element('div', className);
    container.append(...cards);
    this.body.append(element('h3', 'shop__section', title), container);
  }

  /** The featured pack on its stage, the other packs as cards, the pieces of no pack one by one. */
  private renderHome(context: ShopCardContext): void {
    const featured = bundleOf(FEATURED_PACK);
    if (featured !== undefined) this.body.append(heroCard(featured, context, true));
    const others = onSale().filter(
      (offer) => offer.items.length > 1 && offer.pack !== FEATURED_PACK,
    );
    this.section(
      'Autres packs',
      'shop__packs',
      others.map((offer) => packCard(offer, context)),
    );
    const alone = onSale().filter((offer) => offer.items.length === 1 && offer.pack === undefined);
    this.section(
      'À la pièce',
      'shop__grid',
      alone.map((offer) => pieceCard(offer, context)),
    );
  }

  /** A pack's page: the way back, its stage, then each of its pieces to buy on its own. */
  private renderPack(pack: ShopPack, context: ShopCardContext): void {
    const bar = element('div', 'shop__back');
    bar.append(
      button('shop__back-button', '← Retour à la boutique', () => {
        this.show(null);
      }),
    );
    this.body.append(bar);
    const bundle = bundleOf(pack);
    if (bundle !== undefined) this.body.append(heroCard(bundle, context, false));
    const singles = onSale().filter((offer) => offer.pack === pack && offer.items.length === 1);
    this.section(
      'Les pièces à l’unité',
      'shop__grid',
      singles.map((offer) => pieceCard(offer, context)),
    );
  }

  /** The waiver of the right of withdrawal first (ADR 0044), then the payment. */
  private buy(offer: ShopOffer): void {
    if (this.busy) return;
    this.busy = true;
    this.render();
    new ConsentModal({
      offer,
      onConfirm: () => {
        void this.pay(offer);
      },
      onCancel: () => {
        this.busy = false;
        this.render();
      },
    });
  }

  private async pay(offer: ShopOffer): Promise<void> {
    this.say('Ouverture du paiement…', 'muted');
    this.render();
    try {
      const checkout = await startCheckout(this.options.token, offer.id);
      if (checkout.kind === 'redirect') {
        // No publishable key: Stripe's own page, then back here with the order.
        window.location.assign(checkout.url);
        return;
      }
      this.busy = false;
      this.render();
      if (checkout.kind === 'delivered') {
        // A development server paid for pretend: the items are already in.
        this.celebrate(offer.id);
        return;
      }
      if (checkout.kind === 'paid') {
        // Paid for pretend, but the bag is full: the order says so, and how to claim it.
        this.trackOrder(checkout.orderId);
        return;
      }
      this.say('Paiement en cours…', 'muted');
      new CheckoutModal({
        publishableKey: checkout.publishableKey,
        clientSecret: checkout.clientSecret,
        // Paid in the page; the items still come by the webhook: wait for them.
        onPaid: () => {
          this.trackOrder(checkout.orderId);
        },
        onAbandon: (reason) => {
          // Closed unpaid: the order goes too, so it is not left « waiting ».
          void cancelOrder(this.options.token, checkout.orderId)
            .catch(() => undefined)
            .then(() => this.refreshOrders());
          this.say(
            reason === 'failed'
              ? 'Le paiement n’a pas pu s’ouvrir. Réessaie, ou désactive un bloqueur de publicité.'
              : 'Paiement annulé : rien n’a été débité.',
            'refused',
          );
        },
      });
    } catch (error) {
      this.busy = false;
      this.fail(error);
      this.render();
    }
  }

  /**
   * A purchase is in the bag: the shop steps aside and the thank-you window
   * says so, over the game, so the moment is not a line lost at the bottom.
   */
  private celebrate(offerId: string): void {
    this.close();
    new ThanksModal({
      offerId,
      characterName: this.options.characterName,
      icons: this.options.icons,
      onShowInventory: this.options.onShowInventory,
    });
  }

  private async refreshOrders(): Promise<void> {
    try {
      this.orders = await fetchOrders(this.options.token);
    } catch {
      // The list is a convenience: a server out of reach just shows none.
      this.orders = [];
    }
    if (!this.root.hidden) this.renderOrders();
  }

  /** Orders paid but not in the bag, to claim; recent ones still waiting for Stripe. */
  private renderOrders(): void {
    this.ordersList.replaceChildren();
    const now = Date.now();
    for (const order of this.orders) {
      const waiting =
        order.status === 'pending' && now - Date.parse(order.createdAt) < PENDING_SHOWN_MS;
      if (order.status !== 'paid' && !waiting) continue;
      const row = element('li', 'shop__order');
      const name = getOffer(order.offerId)?.name ?? order.offerId;
      row.append(
        element(
          'span',
          'shop__order-label',
          order.status === 'paid'
            ? uiFormat('{name} : payé, à récupérer', { name })
            : uiFormat('{name} : en attente du paiement', { name }),
        ),
      );
      if (order.status === 'paid') {
        row.append(
          button('shop__claim', 'Récupérer', () => {
            void this.claim(order);
          }),
        );
      }
      this.ordersList.append(row);
    }
    this.ordersList.hidden = this.ordersList.childElementCount === 0;
  }

  private async claim(order: ShopOrder): Promise<void> {
    try {
      const claimed = await claimOrder(this.options.token, order.id);
      const name = getOffer(claimed.offerId)?.name ?? claimed.offerId;
      this.say(uiFormat('Livré dans ton sac : {name}.', { name }), 'success');
      this.celebrate(claimed.offerId);
    } catch (error) {
      this.fail(error);
    }
    await this.refreshOrders();
  }
}
